import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import FriendNoteEditor from './FriendNoteEditor'

/**
 * EnhancedUserProfile - Full Profile Modal
 *
 * When viewing YOUR OWN profile: Shows editable profile with avatar/banner uploads
 * When viewing SOMEONE ELSE: Shows read-only profile with friend actions
 *
 * @param {string} userId - Current user's ID
 * @param {string} profileUserId - ID of the profile to display
 * @param {object} currentUserProfile - Current user's profile data
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {function} onUpdateProfile - Callback when profile is updated (for syncing avatar to dock)
 * @param {function} onStartDM - Callback to start a DM
 * @param {function} onStartVoiceCall - Callback to start a voice call
 * @param {function} onStartVideoCall - Callback to start a video call
 */
export default function EnhancedUserProfile({
  userId,
  profileUserId,
  currentUserProfile,
  isOpen,
  onClose,
  onUpdateProfile,
  onStartDM,
  onStartVoiceCall,
  onStartVideoCall,
}) {
  const [profile, setProfile] = useState(null)
  const [isFriend, setIsFriend] = useState(false)
  const [loading, setLoading] = useState(true)

  // Edit mode state (only for own profile)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    display_name: '',
    bio: '',
    custom_status: '',
    pronouns: ''
  })

  // Upload states
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [bannerPreview, setBannerPreview] = useState(null)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [savingProfile, setSavingProfile] = useState(false)

  // File input refs
  const avatarInputRef = useRef(null)
  const bannerInputRef = useRef(null)

  const isOwnProfile = userId === profileUserId
  const BIO_MAX_LENGTH = 500

  useEffect(() => {
    if (profileUserId && isOpen) {
      if (userId === profileUserId && currentUserProfile) {
        setProfile(currentUserProfile)
        setFormData({
          display_name: currentUserProfile.display_name || currentUserProfile.username || '',
          bio: currentUserProfile.bio || '',
          custom_status: currentUserProfile.custom_status || '',
          pronouns: currentUserProfile.pronouns || ''
        })
        setAvatarPreview(currentUserProfile.avatar_url)
        setBannerPreview(currentUserProfile.banner_url)
        setLoading(false)
        // Auto-enable edit mode when viewing own profile
        setIsEditing(true)
      } else {
        loadProfile()
      }
      checkFriendship()
    }
  }, [profileUserId, isOpen, userId, currentUserProfile])

  const loadProfile = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', profileUserId)
        .single()

      if (error) throw error
      setProfile(data)
      setAvatarPreview(data.avatar_url)
      setBannerPreview(data.banner_url)
    } catch (err) {
      console.error('Error loading profile:', err)
    } finally {
      setLoading(false)
    }
  }

  const checkFriendship = async () => {
    if (userId === profileUserId) {
      setIsFriend(false)
      return
    }

    try {
      const { data } = await supabase
        .from('friendships')
        .select('*')
        .or(`and(user_id.eq.${userId},friend_id.eq.${profileUserId}),and(user_id.eq.${profileUserId},friend_id.eq.${userId})`)
        .eq('status', 'accepted')
        .maybeSingle()

      setIsFriend(!!data)
    } catch (err) {
      console.error('Error checking friendship:', err)
    }
  }

  const showToast = (message, type) => {
    const toast = document.createElement('div')
    toast.className = `fixed bottom-4 right-4 px-6 py-3 rounded-gjyl shadow-gjyl-lift z-[100] animate-slide-up ${
      type === 'success' ? 'bg-gjyl-success text-white' : 'bg-gjyl-danger text-white'
    }`
    toast.textContent = message
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 3000)
  }

  // Handle avatar upload
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      showToast('Please upload an image file (PNG, JPG, GIF)', 'error')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast('Avatar must be under 5MB', 'error')
      return
    }

    try {
      setUploadingAvatar(true)

      // Create preview immediately
      const previewUrl = URL.createObjectURL(file)
      setAvatarPreview(previewUrl)

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop()
      const fileName = `${profile.id}-${Date.now()}.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { cacheControl: '3600', upsert: false })

      if (uploadError) {
        if (uploadError.message.includes('Bucket not found')) {
          showToast('Avatar storage not configured. Please contact administrator.', 'error')
        } else {
          throw uploadError
        }
        setAvatarPreview(profile?.avatar_url)
        return
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName)

      // Delete old avatar
      if (profile.avatar_url?.includes('supabase')) {
        const oldFileName = profile.avatar_url.split('/').pop()
        await supabase.storage.from('avatars').remove([oldFileName])
      }

      // Update profile in database
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', profile.id)

      if (updateError) throw updateError

      // Update local state and sync to parent (updates dock avatar)
      const updatedProfile = { ...profile, avatar_url: publicUrl }
      setProfile(updatedProfile)
      setAvatarPreview(publicUrl)
      onUpdateProfile?.(updatedProfile)

      showToast('Avatar updated!', 'success')

    } catch (err) {
      console.error('Error uploading avatar:', err)
      showToast('Failed to upload avatar', 'error')
      setAvatarPreview(profile?.avatar_url)
    } finally {
      setUploadingAvatar(false)
    }
  }

  // Handle banner upload
  const handleBannerChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      showToast('Please upload an image file (PNG, JPG, GIF)', 'error')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      showToast('Banner must be under 10MB', 'error')
      return
    }

    try {
      setUploadingBanner(true)

      const previewUrl = URL.createObjectURL(file)
      setBannerPreview(previewUrl)

      const fileExt = file.name.split('.').pop()
      const fileName = `${profile.id}-${Date.now()}.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('banners')
        .upload(fileName, file, { cacheControl: '3600', upsert: false })

      if (uploadError) {
        if (uploadError.message.includes('Bucket not found')) {
          showToast('Banner storage not configured. Please contact administrator.', 'error')
        } else {
          throw uploadError
        }
        setBannerPreview(profile?.banner_url)
        return
      }

      const { data: { publicUrl } } = supabase.storage
        .from('banners')
        .getPublicUrl(fileName)

      if (profile.banner_url?.includes('supabase')) {
        const oldFileName = profile.banner_url.split('/').pop()
        await supabase.storage.from('banners').remove([oldFileName])
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ banner_url: publicUrl })
        .eq('id', profile.id)

      if (updateError) throw updateError

      const updatedProfile = { ...profile, banner_url: publicUrl }
      setProfile(updatedProfile)
      setBannerPreview(publicUrl)
      onUpdateProfile?.(updatedProfile)

      showToast('Banner updated!', 'success')

    } catch (err) {
      console.error('Error uploading banner:', err)
      showToast('Failed to upload banner', 'error')
      setBannerPreview(profile?.banner_url)
    } finally {
      setUploadingBanner(false)
    }
  }

  // Save profile text fields
  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true)

      if (formData.bio.length > BIO_MAX_LENGTH) {
        showToast(`Bio must be ${BIO_MAX_LENGTH} characters or less`, 'error')
        return
      }

      const updates = {
        display_name: formData.display_name.trim() || profile.username,
        bio: formData.bio.trim(),
        custom_status: formData.custom_status.trim(),
        pronouns: formData.pronouns.trim()
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', profile.id)

      if (error) throw error

      const updatedProfile = { ...profile, ...updates }
      setProfile(updatedProfile)
      onUpdateProfile?.(updatedProfile)

      showToast('Profile saved!', 'success')

    } catch (err) {
      console.error('Error saving profile:', err)
      showToast('Failed to save profile', 'error')
    } finally {
      setSavingProfile(false)
    }
  }

  const addFriend = async () => {
    if (!profile) return

    try {
      const { error } = await supabase
        .from('friendships')
        .insert({
          user_id: userId,
          friend_id: profileUserId,
          status: 'pending'
        })

      if (error) throw error
      showToast(`Friend request sent to ${profile.username}!`, 'success')
    } catch (err) {
      console.error('Error adding friend:', err)
      showToast('Failed to send friend request', 'error')
    }
  }

  const copyFriendCode = async () => {
    if (!profile?.friend_code) return
    try {
      await navigator.clipboard.writeText(profile.friend_code)
      showToast('Friend code copied!', 'success')
    } catch (err) {
      showToast('Failed to copy friend code', 'error')
    }
  }

  const getDefaultAvatar = () => {
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile?.id || 'default'}`
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      {/* Clickable backdrop to close modal */}
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative bg-gjyl-slate-900/95 backdrop-blur-gjyl border-2 border-gjyl-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in shadow-gjyl-lift-strong">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gjyl-indigo-500"></div>
          </div>
        ) : profile ? (
          <>
            {/* Banner Section */}
            <div
              className="h-40 relative overflow-hidden group"
              style={{
                backgroundColor: profile.theme_color || '#6366f1',
                backgroundImage: bannerPreview ? `url(${bannerPreview})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              {/* Banner upload overlay (own profile only) */}
              {isOwnProfile && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none group-hover:pointer-events-auto">
                  <button
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={uploadingBanner}
                    className="px-4 py-2 bg-gjyl-slate-800 hover:bg-gjyl-slate-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 pointer-events-auto"
                  >
                    {uploadingBanner ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Change Banner
                      </>
                    )}
                  </button>
                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleBannerChange}
                  />
                </div>
              )}

              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white rounded-full transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Future: Background effects placeholder */}
              {isOwnProfile && (
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/40 backdrop-blur-sm rounded text-xs text-white/70">
                  Background effects coming soon
                </div>
              )}
            </div>

            {/* Profile Content */}
            <div className="px-6 pb-6">
              {/* Avatar Section */}
              <div className="relative -mt-16 mb-4">
                <div className="relative inline-block group">
                  <div
                    className={`w-28 h-28 rounded-gjyl-xl ring-4 ring-gjyl-slate-900 overflow-hidden bg-gjyl-slate-800 ${isOwnProfile ? 'cursor-pointer' : ''}`}
                    onClick={isOwnProfile ? () => avatarInputRef.current?.click() : undefined}
                    role={isOwnProfile ? 'button' : undefined}
                    aria-label={isOwnProfile ? 'Click to change avatar' : undefined}
                    tabIndex={isOwnProfile ? 0 : undefined}
                    onKeyDown={isOwnProfile ? (e) => e.key === 'Enter' && avatarInputRef.current?.click() : undefined}
                  >
                    <img
                      src={avatarPreview || getDefaultAvatar()}
                      alt={profile.username}
                      className="w-full h-full object-cover"
                    />
                    {/* Hover overlay for visual feedback (own profile only) */}
                    {isOwnProfile && (
                      <div className="absolute inset-0 bg-black/50 rounded-gjyl-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        {uploadingAvatar ? (
                          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Hidden file input for avatar upload */}
                  {isOwnProfile && (
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarChange}
                      aria-label="Upload avatar image"
                    />
                  )}

                  {/* Persistent camera badge (own profile only) - visible hint that avatar is editable */}
                  {isOwnProfile && !uploadingAvatar && (
                    <div
                      className="absolute -top-1 -right-1 w-7 h-7 bg-gjyl-indigo-500 rounded-full border-2 border-gjyl-slate-900 flex items-center justify-center cursor-pointer hover:bg-gjyl-indigo-400 transition-colors shadow-lg z-10"
                      onClick={(e) => { e.stopPropagation(); avatarInputRef.current?.click(); }}
                      title="Change avatar"
                      aria-label="Change avatar"
                      data-testid="avatar-edit-button"
                    >
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  )}

                  {/* Status indicator */}
                  <div
                    className={`absolute bottom-1 right-1 w-6 h-6 rounded-full border-4 border-gjyl-slate-900 ${
                      profile.status === 'online' ? 'bg-gjyl-success'
                        : profile.status === 'idle' ? 'bg-gjyl-warning'
                        : profile.status === 'dnd' ? 'bg-gjyl-danger'
                        : 'bg-gjyl-slate-600'
                    }`}
                  />
                </div>
              </div>

              {/* Profile Info - Editable for own profile */}
              {isOwnProfile && isEditing ? (
                <div className="space-y-4">
                  {/* Username (read-only) */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Username</label>
                    <div className="px-3 py-2 bg-gjyl-slate-800/50 text-slate-500 rounded-lg border border-gjyl-slate-700/50 text-sm">
                      @{profile.username}
                    </div>
                  </div>

                  {/* Display Name */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Display Name</label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                      placeholder={profile.username}
                      className="w-full px-3 py-2 bg-gjyl-slate-800/50 text-white rounded-lg border border-gjyl-slate-700/50 focus:border-gjyl-indigo-500 focus:ring-1 focus:ring-gjyl-indigo-500/30 outline-none transition-all text-sm"
                    />
                  </div>

                  {/* Pronouns */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Pronouns</label>
                    <input
                      type="text"
                      value={formData.pronouns}
                      onChange={(e) => setFormData({ ...formData, pronouns: e.target.value })}
                      placeholder="e.g., they/them"
                      className="w-full px-3 py-2 bg-gjyl-slate-800/50 text-white rounded-lg border border-gjyl-slate-700/50 focus:border-gjyl-indigo-500 focus:ring-1 focus:ring-gjyl-indigo-500/30 outline-none transition-all text-sm"
                    />
                  </div>

                  {/* Custom Status */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Status</label>
                    <input
                      type="text"
                      value={formData.custom_status}
                      onChange={(e) => setFormData({ ...formData, custom_status: e.target.value })}
                      placeholder="What's on your mind?"
                      maxLength={100}
                      className="w-full px-3 py-2 bg-gjyl-slate-800/50 text-white rounded-lg border border-gjyl-slate-700/50 focus:border-gjyl-indigo-500 focus:ring-1 focus:ring-gjyl-indigo-500/30 outline-none transition-all text-sm"
                    />
                  </div>

                  {/* Bio */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">About Me</label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      placeholder="Tell us about yourself..."
                      rows={4}
                      maxLength={BIO_MAX_LENGTH}
                      className="w-full px-3 py-2 bg-gjyl-slate-800/50 text-white rounded-lg border border-gjyl-slate-700/50 focus:border-gjyl-indigo-500 focus:ring-1 focus:ring-gjyl-indigo-500/30 outline-none transition-all text-sm resize-none"
                    />
                    <div className="text-xs text-slate-500 text-right mt-1">
                      {formData.bio.length}/{BIO_MAX_LENGTH}
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleSaveProfile}
                      disabled={savingProfile}
                      className="flex-1 px-6 py-3 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-400 text-white font-medium rounded-gjyl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {savingProfile ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                    <button
                      onClick={onClose}
                      className="px-6 py-3 glass hover:glass-strong text-white font-medium rounded-gjyl transition-all"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                /* Read-only view (other users' profiles) */
                <>
                  <div className="mb-4">
                    <h2 className="text-2xl font-bold text-white">{profile.display_name || profile.username}</h2>
                    <p className="text-slate-400">@{profile.username}</p>
                    {profile.pronouns && (
                      <div className="text-sm text-slate-500 mt-1">{profile.pronouns}</div>
                    )}
                    {profile.custom_status && (
                      <div className="mt-2 px-3 py-1.5 glass rounded-gjyl inline-block">
                        <span className="text-sm text-slate-300">{profile.custom_status}</span>
                      </div>
                    )}
                  </div>

                  {profile.bio && (
                    <div className="mt-4 p-4 glass rounded-gjyl">
                      <h3 className="text-sm font-semibold text-gjyl-indigo-400 uppercase tracking-wide mb-2">About Me</h3>
                      <p className="text-slate-300 whitespace-pre-wrap">{profile.bio}</p>
                    </div>
                  )}

                  {profile.friend_code && (
                    <div className="mt-4 p-4 glass rounded-gjyl">
                      <h3 className="text-sm font-semibold text-gjyl-indigo-400 uppercase tracking-wide mb-2">Friend Code</h3>
                      <div className="flex items-center gap-3">
                        <code className="flex-1 px-3 py-2 bg-gjyl-slate-900/50 rounded-gjyl text-gjyl-accent font-mono text-sm">
                          {profile.friend_code}
                        </code>
                        <button onClick={copyFriendCode} className="px-3 py-2 glass hover:glass-strong rounded-gjyl text-white transition-all">
                          Copy
                        </button>
                      </div>
                    </div>
                  )}

                  {profile.created_at && (
                    <div className="mt-4 p-4 glass rounded-gjyl">
                      <h3 className="text-sm font-semibold text-gjyl-indigo-400 uppercase tracking-wide mb-2">Member Since</h3>
                      <p className="text-slate-300">
                        {new Date(profile.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  )}

                  {isFriend && userId !== profileUserId && (
                    <FriendNoteEditor
                      userId={userId}
                      friendId={profileUserId}
                      friendUsername={profile.username}
                    />
                  )}

                  {/* Actions */}
                  <div className="mt-6 flex flex-wrap gap-3">
                    {!isFriend && userId !== profileUserId && (
                      <button
                        onClick={addFriend}
                        className="flex-1 px-6 py-3 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-400 text-white font-medium rounded-gjyl transition-all hover:scale-105 active:scale-95 shadow-gjyl-glow"
                      >
                        Add Friend
                      </button>
                    )}

                    {isFriend && (
                      <>
                        <button
                          onClick={() => { onStartDM?.(profileUserId); onClose() }}
                          className="flex-1 px-6 py-3 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-400 text-white font-medium rounded-gjyl transition-all"
                        >
                          Message
                        </button>
                        <button
                          onClick={() => { onStartVoiceCall?.(profileUserId); onClose() }}
                          className="flex-1 px-6 py-3 glass hover:glass-strong text-white font-medium rounded-gjyl transition-all"
                        >
                          Voice Call
                        </button>
                        <button
                          onClick={() => { onStartVideoCall?.(profileUserId); onClose() }}
                          className="flex-1 px-6 py-3 glass hover:glass-strong text-white font-medium rounded-gjyl transition-all"
                        >
                          Video Call
                        </button>
                      </>
                    )}

                    <button
                      onClick={onClose}
                      className="flex-1 px-6 py-3 glass hover:glass-strong text-white font-medium rounded-gjyl transition-all"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="p-12 text-center">
            <p className="text-slate-400">Profile not found</p>
            <button
              onClick={onClose}
              className="mt-4 px-6 py-2 glass hover:glass-strong text-white rounded-gjyl transition-all"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
